import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBell, 
  faTimes, 
  faShoppingCart, 
  faExclamationTriangle, 
  faFlask, 
  faClipboardCheck, 
  faUserMd, 
  faEnvelope, 
  faBox,
  faVialCircleCheck,
  faVial,
  faCheckCircle,
  faPhoneAlt,
  faPhoneSquare,
  faTabletAndroid,
  faMobileAndroid,
  faShoppingBasket
} from '@fortawesome/free-solid-svg-icons';
import { useNotifications } from './NotificationService';
import './Notifications.css';

const NotificationBell = ({ userId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleNotifications, setVisibleNotifications] = useState([]);
  const [activeFilter, setActiveFilter] = useState('unread');
  const [isClosing, setIsClosing] = useState(false);
  const initialUnreadStatuses = useRef(new Map());
  const modalRef = useRef();
  const isInitialModalLoadRef = useRef(true);
  
  const {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    refreshNotifications,
    activeToasts,
    removeToast
  } = useNotifications(userId);

  // When modal is first opened, reset status tracking and create copy
  useEffect(() => {
    if (isModalOpen) {
      if (isInitialModalLoadRef.current && notifications.length > 0) {
        // First time opening the modal in this session
        isInitialModalLoadRef.current = false;
        initialUnreadStatuses.current.clear();
        
        // Create a visual copy preserving original status
        const visualCopy = notifications.map(notification => {
          // Store the current status from server
          initialUnreadStatuses.current.set(notification.id, notification.Status);
          
          return {
            ...notification,
            originalStatus: notification.Status
          };
        });
        
        setVisibleNotifications(visualCopy);
        
        // Mark all as read on the server
        if (unreadCount > 0) {
          markAllAsRead();
        }
      } else if (notifications.length > 0) {
        // Update for new notifications while keeping existing status info
        const updatedNotifications = notifications.map(notification => {
          // Check if we've seen this notification before
          const hasExistingStatus = initialUnreadStatuses.current.has(notification.id);
          
          // If it's a new notification, track its status
          if (!hasExistingStatus) {
            initialUnreadStatuses.current.set(notification.id, notification.Status);
          }
          
          return {
            ...notification,
            // Use stored status if available, otherwise use current
            originalStatus: hasExistingStatus 
              ? initialUnreadStatuses.current.get(notification.id) 
              : notification.Status
          };
        });
        
        setVisibleNotifications(updatedNotifications);
      }
    } else {
      // When modal is closed, reset initial load flag
      isInitialModalLoadRef.current = true;
    }
  }, [isModalOpen, notifications, unreadCount, markAllAsRead]);

  // Handle clicking outside to close modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModalOpen]);

  const openModal = () => {
    setIsModalOpen(true);
    setActiveFilter('unread'); // Always reset to unread when opening
  };

  const closeModal = () => {
    // Start closing animation
    setIsClosing(true);
    
    // Clear the status tracking on close
    initialUnreadStatuses.current.clear();
    
    setTimeout(() => {
      setIsModalOpen(false);
      setIsClosing(false);
      setVisibleNotifications([]); // Reset the visible notifications
    }, 300); // Animation duration
  };

  const handleNotificationClick = (notification) => {
    // Update the visual status
    setVisibleNotifications(prev => 
      prev.map(n => n.id === notification.id ? {...n, originalStatus: 'Read'} : n)
    );
    
    // Also update our tracking map
    initialUnreadStatuses.current.set(notification.id, 'Read');
  };

  const getCategoryIcon = (origin) => {
    switch (origin?.toLowerCase()) {
      case 'ordering system':
        return faVial;
      case 'validation':
        return faCheckCircle;
      case 'shop':
        return faShoppingBasket;  
      case 'app':
        return faMobileAndroid;
      default:
        return faEnvelope;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const filteredNotifications = () => {
    if (activeFilter === 'unread') {
      return visibleNotifications.filter(notification => notification.originalStatus === 'Unread');
    } else {
      // For read notifications, limit to most recent 10
      return visibleNotifications
        .filter(notification => notification.originalStatus === 'Read' || notification.Status === 'Read')
        .sort((a, b) => new Date(b.Date) - new Date(a.Date))
        .slice(0, 10);
    }
  };

  return (
    <div className="notification-bell-container">
      <div className="bell-icon" onClick={openModal}>
        <FontAwesomeIcon icon={faBell} />
        {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
      </div>

      {isModalOpen && (
        <div className={`szap-notification-modal ${isClosing ? 'closing' : ''}`} ref={modalRef}>
          <div className="szap-notification-filters">
          <div className="szap-notification-filtersbackground">

            <button 
              className={`szap-filter-btn ${activeFilter === 'unread' ? 'active' : ''}`} 
              onClick={() => setActiveFilter('unread')}
            >
              Unread
            </button>
            <button 
              className={`szap-filter-btn ${activeFilter === 'read' ? 'active' : ''}`} 
              onClick={() => setActiveFilter('read')}
            >
              Read
            </button>
          </div>
          </div>

          <div className="szap-notification-list">
            {loading && <div className="szap-notification-loading">Loading...</div>}
            
            {error && <div className="szap-notification-error">Error: {error}</div>}
            
            {!loading && !error && filteredNotifications().length === 0 && (
              <div className="szap-no-notifications">
                {activeFilter === 'unread' ? 'No unread notifications' : 'No recent notifications'}
              </div>
            )}
            
            {!loading && !error && filteredNotifications().map(notification => (
              <div 
                key={notification.id} 
                className={`szap-notification-item ${notification.originalStatus === 'Unread' ? 'unread' : ''}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="szap-notification-icon">
                  <FontAwesomeIcon icon={getCategoryIcon(notification.Origin)} />
                </div>
                <div className="szap-notification-content">
                  <div className="szap-notification-title">{notification.Origin}</div>
                  <div className="szap-notification-message">{notification.Content}</div>
                  <div className="szap-notification-time">
                    {formatDate(notification.Date)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stacked Toast Notifications */}
      <div className="szap-notification-toast-container">
        {activeToasts.map(toast => (
          <div 
            key={toast.toastId} 
            className="szap-notification-toast"
            data-toast-id={toast.toastId}
          >
            <div className="szap-notification-toast-icon">
              <FontAwesomeIcon icon={getCategoryIcon(toast.notification.Origin)} />
            </div>
            <div className="szap-notification-toast-content">
              <div className="szap-notification-toast-title">{toast.notification.Origin}</div>
              <div className="szap-notification-toast-message">
                {toast.notification.Content}
              </div>
            </div>
            <button 
              className="szap-notification-toast-close"
              onClick={() => removeToast(toast.toastId)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NotificationBell;
